import React, { useRef, useState, useCallback } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import classnames from 'classnames/bind'

import GradientHeader from 'components/shared/GradientHeader'
import Image from 'components/shared/Image'
import Ratio from 'components/shared/Ratio'
import { Title, Text } from 'components/typo'
import RichText from 'components/typo/RichText'

import useOnResize from 'hooks/useOnResize'

import { twoDigits } from 'services/helpers.js'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const StackingCards = ({ title, className, cards, gradientHeader }) => {
  const [minHeight, setMinHeight] = useState('auto')
  const length = cards ? cards.length : 0
  const cardRefs = useRef([])
  cardRefs.current = []

  useIsomorphicLayoutEffect(() => {
    if (minHeight !== 'auto') return
    const heights = cardRefs.current.map((card) => card.offsetHeight)
    setMinHeight(Math.max(...heights))
  }, [minHeight])

  const onResize = useCallback(() => setMinHeight('auto'), [])
  useOnResize(onResize, [])

  return (
    <GradientHeader
      {...gradientHeader}
      wrapperType="innerWrapper"
      className={cx(css.StackingCards, className)}
    >
      <div className={css.contentWrapper}>
        <Title className={css.title}>{title}</Title>
        {cards && (
          <ul className={css.cardsContainer}>
            {cards.map(({ title, text, icon }, index) => (
              <li key={index} className={css.card}>
                <div
                  className={css.cardContainer}
                  style={{
                    paddingTop: `${20 * index}px`,
                    paddingBottom: `${20 * length - index * 20}px`,
                  }}
                >
                  <div
                    ref={(el) => el && cardRefs.current.push(el)}
                    className={css.cardWrapper}
                    style={{ minHeight }}
                  >
                    <div className={css.cardHeader}>
                      {icon && (
                        <div className={css.icon}>
                          <Ratio ratio={1}>
                            <Image
                              fluid
                              fit="contain"
                              className={css.image}
                              {...icon}
                            />
                          </Ratio>
                        </div>
                      )}
                      <Text
                        className={css.step}
                        theme="dark"
                        tag="div"
                        size="extra-small"
                      >{`${twoDigits(index + 1)}/${twoDigits(length)}`}
                      </Text>
                    </div>
                    {title && (
                      <Title size="small" className={css.cardTitle}>
                        {title}
                      </Title>
                    )}
                    {text && (
                      <RichText
                        text={text}
                        type="Text"
                        className={css.cardText}
                      />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </GradientHeader>
  )
}

StackingCards.defaultProps = {
  cards: [],
  theme: 'blue',
  layout: 'gradient-left',
  decoration: { variantId: 0, rotate: 180 },
}

export default StackingCards
